import { DataUpdatePayload, StrictDataUpdateV1Payload, TripleUpdatePayload } from "./data-update-schema"
import { Quad, Term, Literal, NamedNode } from "n3"

export type Logging = {
  graph: string,
  customStatements: string,
}

/**
 * Quads -> triple payloads, one for one. No de-duplication, no cancelling of a
 * triple that appears in both lists: callers that want that (see
 * removeDuplicatePayloads) apply it themselves.
 */
export function triplePayloadsFromN3Quads(quads: Quad[]) {
  const triplePayloads: TripleUpdatePayload[] = []
  for (const quad of quads) {
    triplePayloads.push({
      graph: (quad.graph as NamedNode).id,
      subject: (quad.subject as NamedNode).id,
      predicate: (quad.predicate as NamedNode).id,
      object: toObjectPayload(quad.object)
    })
  }
  return triplePayloads
}

export function dataUpdatePayloadFromN3Quads(args: {
  updates: Quad[],
  deletes: Quad[],
  logging: Logging | undefined,
}) {
  const updatesPayload = triplePayloadsFromN3Quads(args.updates ?? [])
  const deletesPayload = triplePayloadsFromN3Quads(args.deletes ?? [])
  const [ updates, deletes ] = removeDuplicatePayloads(updatesPayload, deletesPayload)
  return {
    updates: updates,
    delete: deletes,
    logging: args.logging,
  } satisfies DataUpdatePayload
}

export function strictDataUpdatePayload(args: {
  updates: Quad[],
  deletes: Quad[],
  logging: Logging | undefined,
}): StrictDataUpdateV1Payload {
  const updatesPayload = triplePayloadsFromN3Quads(args.updates ?? [])
  const deletesPayload = triplePayloadsFromN3Quads(args.deletes ?? [])
  const [ updates, deletes ] = removeDuplicatePayloads(updatesPayload, deletesPayload)
  return {
    variant: 'strict-v1',
    inserts: updates,
    deletes: deletes,
    logging: args.logging,
  }
}

function toObjectPayload(n3node: Term) {
  if (n3node.termType === "Literal") {
    const literal = n3node as Literal
    if (literal.language) {
      return {
        type: "literal",
        value: literal.value,
        languageTag: literal.language
      } as const
    } else {
      return {
        type: "literal",
        value: literal.value,
        dataType: literal.datatypeString
      } as const
    }
  } else if (n3node.termType === "NamedNode") {
    const namedNode = n3node as NamedNode
    return {
      type: "uri",
      value: namedNode.id,
      dataType: ""
    } as const
  } else {
    throw new Error("N3 node is neither NamedNode or Literal: " + n3node.termType)
  }
}

export function removeDuplicatePayloads(
  updates: TripleUpdatePayload[],
  deletes: TripleUpdatePayload[]
) {
  const updateKeys = new Set(updates.map(payloadKey))
  const deleteKeys = new Set(deletes.map(payloadKey))

  const updatesOnly = updateKeys.difference(deleteKeys)
  const deletesOnly = deleteKeys.difference(updateKeys)

  const filteredUpdates = updates.filter(p => updatesOnly.has(payloadKey(p)))
  const filteredDeletes = deletes.filter(p => deletesOnly.has(payloadKey(p)))

  return [ filteredUpdates, filteredDeletes ] as const

  function payloadKey(payload: TripleUpdatePayload): string {
    const { graph, subject, predicate, object } = payload
    const objStr =
      object.type === "literal"
        ? `L:${object.value}^^${object.dataType}`
        : `U:${object.value}`
    return `${graph} ${subject} ${predicate} ${objStr}`
  }
}
